import React from 'react';
import { Box, Typography, Slider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
  container: {
    width: '94%',
    marginBottom: theme.spacing(2),
    padding: '0 8px',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      top: -8,
      bottom: -8,
      pointerEvents: 'none'
    }
  },
  label: {
    color: '#21303a',
    fontSize: '16px',
    fontWeight: 500,
    marginBottom: theme.spacing(1.5),
    display: 'block',
    textAlign: 'center'
  },
  slider: {
    color: '#6f42f5',
    height: 6,
    padding: '8px 0',
    marginLeft: '3%',
    '& .MuiSlider-track': {
      border: 'none',
      backgroundColor: '#6f42f5',
      height: 6,
      borderRadius: 3
    },
    '& .MuiSlider-rail': {
      backgroundColor: '#6f42f5',
      height: 6,
      borderRadius: 3
    },
    '& .MuiSlider-thumb': {
      height: 20,
      width: 20,
      backgroundColor: '#6f42f5',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      '&:hover': {
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
        transform: 'scale(1.1)'
      },
      '&:active': {
        transform: 'scale(1.05)'
      },
      '&:focus': {
        boxShadow: '0 0 0 8px rgba(111, 66, 245, 0.16)'
      }
    },
    '& .MuiSlider-valueLabel': {
      backgroundColor: '#757575 !important',
      fontSize: '12px',
      marginLeft: '10px',
      fontWeight: 500,
      zIndex: 1,
      position: 'relative',
      '&:before': {
        border: '1px solid #757575'
      }
    },
    '& .MuiSlider-valueLabel *': {
      backgroundColor: '#757575 !important',
      zIndex: 1
    },
    '& .MuiSlider-mark': {
      backgroundColor: '#6f42f5',
      height: 4,
      width: 4,
      borderRadius: '50%',
      marginTop: 1,
      '&:nth-child(1), &:nth-child(9)': {
        display: 'none !important'
      }
    },
    '& .MuiSlider-markActive': {
      backgroundColor: '#FFFFFF'
    },
    '& .MuiSlider-markLabel': {
      color: '#21303a',
      fontSize: '14px',
      fontWeight: 400,
      marginTop: 8
    }
  },
  valueDisplay: {
    textAlign: 'center',
    marginTop: theme.spacing(1)
  },
  valueText: {
    color: '#6f42f5',
    fontSize: '14px',
    fontWeight: 500,
    backgroundColor: 'rgba(111, 66, 245, 0.1)',
    padding: '4px 8px',
    borderRadius: '4px',
    display: 'inline-block',
    position: 'relative',
    bottom: '30px'
  },
  disabled: {
    '& .MuiSlider-track': {
      backgroundColor: '#cccccc'
    },
    '& .MuiSlider-rail': {
      backgroundColor: '#f5f5f5'
    },
    '& .MuiSlider-thumb': {
      backgroundColor: '#cccccc',
      cursor: 'not-allowed'
    },
    '& .MuiSlider-thumb:hover': {
      transform: 'none',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
    }
  }
}));

interface MUIRangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  step?: number;
  disabled?: boolean;
  label?: string;
  showValueLabels?: boolean;
}

const MUIRangeSlider: React.FC<MUIRangeSliderProps> = ({
  min,
  max,
  value,
  onChange,
  step = 1,
  disabled = false,
  label = 'Select Recommended Hours',
  showValueLabels = true
}) => {
  const classes = useStyles();

  const handleChange = (_: any, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      onChange([newValue[0], newValue[1]]);
    }
  };

  return (
    <Box className={classes.container}>
      <Typography className={classes.label} style={{ marginBottom: '0px' }}>
        {label}
      </Typography>

      <Slider
        className={`${classes.slider} ${disabled ? classes.disabled : ''}`}
        value={value}
        onChange={handleChange}
        valueLabelDisplay={
          showValueLabels && (value[0] !== min || value[1] !== max)
            ? 'on'
            : 'off'
        }
        valueLabelFormat={val => `${val}`}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        track="normal"
        marks={[
          { value: min, label: `${min}` },
          { value: min + 5 },
          { value: min + 10 },
          { value: min + 15 },
          { value: min + 20 },
          { value: min + 25 },
          { value: min + 30 },
          { value: min + 35 },
          { value: max, label: `${max}` }
        ]}
      />
    </Box>
  );
};

export default MUIRangeSlider;
